import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { ChevronIcon } from "@images/svgIcons/chevronIcon"
import { CoachNoteIcon } from "@images/svgIcons/coachNoteIcon"
import { EditIcon } from "@images/svgIcons/editIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { SessionBlockAthleteNoteFormikType } from "@modules/sessionBlock/sessionBlockFormikTypes"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { useSessionSidebar } from "@modules/sessions/hooks/useSessionSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { ThemeMode } from "@modules/ui/styles/theme"
import { MenuItem } from "primereact/menuitem"
import { useState } from "react"
import { useTheme } from "styled-components"

type Props = {
  block: SessionBlockAthleteNoteFormikType
  blockIndex: number
}

export const NoteForAthletesExistingBloc: React.FC<Props> = ({ block, blockIndex }) => {
  const [isContentDisplayed, setIsContentDisplayed] = useState(true)

  const theme = useTheme()
  const { formik, moveBlock, deleteBlock } = useSessionFormContext()
  const { navigateToNoteForAthletesForm } = useSessionSidebar()

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: word("sessionForm.blocks.move.up"),
          icon: (
            <ArrowIcon
              arrowDirection="top"
              color={blockIndex === 0 ? theme.neutralColor300 : theme.neutralColor900}
              width={16}
              height={16}
            />
          ),
          visible: blockIndex !== 0 && formik.values.blocks.length >= 1,
          command: () => moveBlock(blockIndex, "UP"),
        },
        {
          label: word("sessionForm.blocks.move.down"),
          icon: (
            <ArrowIcon
              arrowDirection="bottom"
              color={blockIndex === formik.values.blocks.length - 1 ? theme.neutralColor300 : theme.neutralColor900}
              width={16}
              height={16}
            />
          ),
          visible: blockIndex < formik.values.blocks.length - 1 && formik.values.blocks.length >= 1,
          command: () => moveBlock(blockIndex, "DOWN"),
        },
        {
          label: word("sessionForm.exercise.remove_exo.button"),
          icon: <TrashIcon color={theme.neutralColor900} width={20} height={20} />,
          command: () => deleteBlock(blockIndex),
        },
      ].filter((item) => item.visible !== false),
    },
  ]

  return (
    <div
      className={
        "flex flex-column p-3 pt-2 pl-1 rounded-big-radius border-1 border-dashed border-neutralColor-200 " +
        (theme.mode === ThemeMode.OWNER ? "bg-neutralColor-50" : "")
      }
    >
      {/* Note card header */}
      <div className="flex flex-row align-items-center justify-content-between">
        <div className="flex flex-row align-items-center justify-content-start">
          <CoachNoteIcon color={theme.neutralColor700} />
          <div className="flex flex-column">
            <HUText color={theme.neutralColor700} fontStyle="TS">
              {word("sessionForm.blocks.note_for_athletes")}
            </HUText>
          </div>
        </div>

        <HUButton
          type="Rounded"
          colorType="Quaternary"
          size="XS"
          icon={{
            icon: () => (
              <ChevronIcon color={theme.neutralColor700} className={isContentDisplayed ? "rotate-180" : "rotate-0"} />
            ),
          }}
          className="cursor-pointer border-1 border-transparent"
          onClick={() => setIsContentDisplayed(!isContentDisplayed)}
        />
      </div>

      {isContentDisplayed && (
        <>
          {/* Note card content */}
          <div style={{ paddingLeft: 50 }}>
            <HURichTextView value={formatTextToRichText(block.content.note)} paragrapheFontStyle="BS" />
          </div>

          {/* Note card footer*/}
          <div className="flex flex-row align-items-center justify-content-end mt-2">
            <EllipsisMenu items={ellipsisItems} style={{ height: "2.2rem", width: "2.2rem" }} />

            <HUButton
              type="Rounded"
              colorType="Primary"
              size="M"
              icon={{
                icon: () => <EditIcon color={theme.neutralColor900} width={16} height={16} />,
              }}
              style={{ height: "2.2rem", width: "2.2rem" }}
              onClick={() => navigateToNoteForAthletesForm(false, blockIndex, block)}
              isTooltipDisabled={false}
              tooltipContent={word("global.edit")}
              tooltipId={"edit-note" + blockIndex}
            />
          </div>
        </>
      )}
    </div>
  )
}
